import { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { search, getFacilityNumbering, getTicketNumbering } from "../lib/api";
import { ApiError } from "../lib/api";
import type { FacilityNumberingConfig, TicketNumberingConfig } from "../lib/api";
import { formatFacilityNumber, formatTicketNumber } from "../lib/format";
import type { SearchResponse, SearchResult } from "../lib/types";

const DEBOUNCE_MS = 250;

const GROUPS: {
  key: keyof Pick<SearchResponse, "tickets" | "tasks" | "articles" | "contacts" | "workOrders" | "assets">;
  label: string;
}[] = [
  { key: "tickets", label: "Tickets" },
  { key: "workOrders", label: "Work orders" },
  { key: "assets", label: "Assets" },
  { key: "tasks", label: "Project tasks" },
  { key: "articles", label: "Articles" },
  { key: "contacts", label: "Contacts" },
];

/** Top-bar global search: debounced query against /api/search with grouped,
 *  navigable results. Only ticket results route in-app today (the only matching
 *  surface in this app); other kinds are shown but not yet linked. */
export function GlobalSearch() {
  const navigate = useNavigate();
  const listId = useId();

  const [q, setQ] = useState("");
  const [results, setResults] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [facFmt, setFacFmt] = useState<FacilityNumberingConfig | null>(null);
  const [ticketFmt, setTicketFmt] = useState<TicketNumberingConfig | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Ticket + facility numbering config (prefix/padding) for formatting codes
  // like "MR00029" in results. Loaded once; best-effort (search still works
  // without them, just showing the bare number).
  useEffect(() => {
    const ctrl = new AbortController();
    getTicketNumbering(ctrl.signal)
      .then((res) => setTicketFmt(res.numbering))
      .catch(() => {
        /* non-fatal: fall back to the raw number */
      });
    getFacilityNumbering(ctrl.signal)
      .then((res) => setFacFmt(res.numbering))
      .catch(() => {
        /* non-fatal: fall back to the raw number */
      });
    return () => ctrl.abort();
  }, []);

  /** Display label for a result — tickets and work orders get their formatted
   *  code prefixed (e.g. "MR00029 · Password reset"). */
  function resultTitle(r: SearchResult): string {
    if (r.number != null && (r.type === "ticket" || r.type === "work_order")) {
      const code =
        r.type === "ticket"
          ? formatTicketNumber(r.number, ticketFmt ?? undefined)
          : formatFacilityNumber(r.number, facFmt ?? undefined);
      return code ? `${code} · ${r.title}` : r.title;
    }
    return r.title;
  }

  // Debounced search whenever the trimmed query changes.
  useEffect(() => {
    const term = q.trim();
    if (term.length === 0) {
      setResults(null);
      setLoading(false);
      setError(null);
      return;
    }
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    const t = window.setTimeout(() => {
      search(term, ctrl.signal)
        .then((res) => {
          if (ctrl.signal.aborted) return;
          setResults(res);
          setLoading(false);
        })
        .catch((err: unknown) => {
          if (ctrl.signal.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
          if (err instanceof ApiError && err.status === 401) return;
          setError(err instanceof Error ? err.message : "Search failed");
          setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      ctrl.abort();
      window.clearTimeout(t);
    };
  }, [q]);

  // Close the results panel on outside click / Escape.
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  function go(r: SearchResult) {
    if (r.type === "contact") return; // no in-app surface for contacts yet
    setOpen(false);
    setQ("");
    // Map each result to the route/deep-link that opens it in this app.
    if (r.type === "ticket") navigate(`/tickets/${r.id}`);
    else if (r.type === "work_order") navigate(`/facilities/${r.id}`);
    else if (r.type === "asset") navigate(`/assets/${r.id}`);
    else if (r.type === "task") navigate(`/action-items?open=${r.id}`);
    else if (r.type === "article") navigate(`/kb?open=${r.id}`);
  }

  const showPanel = open && q.trim().length > 0;

  return (
    <div className="search-wrap" ref={wrapRef}>
      <span className="search-icon" aria-hidden="true">
        🔍
      </span>
      <input
        type="text"
        className="search-input"
        role="combobox"
        aria-expanded={showPanel}
        aria-controls={listId}
        aria-label="Search tickets, work orders, tasks, articles and contacts"
        placeholder="Search…"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />

      {showPanel ? (
        <div className="search-panel" id={listId} role="listbox" aria-label="Search results">
          {loading && !results ? (
            <div className="search-status muted">Searching…</div>
          ) : error ? (
            <div className="search-status" role="alert" style={{ color: "var(--danger)" }}>
              {error}
            </div>
          ) : results && results.total === 0 ? (
            <div className="search-status muted">No matches for “{q.trim()}”.</div>
          ) : results ? (
            <>
              {GROUPS.map(({ key, label }) => {
                const rows = results[key];
                if (rows.length === 0) return null;
                return (
                  <div className="search-group" key={key}>
                    <div className="search-group-head">
                      {label} <span className="faint">{rows.length}</span>
                    </div>
                    {rows.map((r) => (
                      <button
                        key={`${r.type}-${r.id}`}
                        type="button"
                        role="option"
                        aria-selected={false}
                        className={`search-result${r.type === "contact" ? " disabled" : ""}`}
                        onClick={() => go(r)}
                        disabled={r.type === "contact"}
                        title={r.type === "contact" ? "Contacts have no dedicated page yet" : undefined}
                      >
                        <span className="search-result-title">{resultTitle(r)}</span>
                        {r.snippet ? <span className="search-result-snip muted">{r.snippet}</span> : null}
                      </button>
                    ))}
                  </div>
                );
              })}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
